import React, { useState, useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell, LineChart, Line, ReferenceLine
} from 'recharts';
import StatCard from '../common/cards/StatCard';
import { formatCurrency } from '../../utils/formatters';
import { DollarSign, Users, TrendingUp } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#a4de6c', '#8dd1e1'];

// Opções de período para os filtros
const PERIOD_OPTIONS = [
    { label: 'Todo o tempo', value: null },
    { label: '1 mês',        value: 1  },
    { label: '3 meses',      value: 3  },
    { label: '6 meses',      value: 6  },
    { label: '1 ano',        value: 12 },
];

// Limpa o nome do plano para exibição no eixo Y do gráfico (espaço limitado)
const cleanPlanName = (name) => {
    let clean = name
        .replace(/-/g, '')
        .replace(/\+/g, '')
        .replace(/\./g, '')
        .replace(/c\//ig, '')
        .replace(/BANDA LARGA/ig, '')
        .replace(/\s+/g, ' ')
        .trim();
    const wordsToRemove = ['de', 'para', 'com'];
    return clean.split(' ').filter(w => !wordsToRemove.includes(w.toLowerCase())).join(' ');
};

const isDefaultSeller = (fullName) => {
    const normalized = (fullName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return normalized.includes('vendedor padrao') || normalized.includes('vend. padrao');
};

// Select de filtro de período
function PeriodFilterDropdown({ value, onChange, isDark }) {
    const handleChange = (e) => {
        const val = e.target.value === 'null' ? null : Number(e.target.value);
        onChange(val);
    };
    return (
        <select
            value={value === null ? 'null' : value}
            onChange={handleChange}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                    ? 'bg-slate-700 text-slate-200 border-slate-600'
                    : 'bg-white text-slate-700 border-slate-300'
            }`}
        >
            {PERIOD_OPTIONS.map(opt => (
                <option key={String(opt.value)} value={opt.value === null ? 'null' : opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    );
}

export default function CommercialDashboard({ biStats, theme }) {
    const isDark = theme === 'dark';
    const gridColor = isDark ? '#334155' : '#e2e8f0';
    const tickColor = isDark ? '#94a3b8' : '#475569';

    const {
        arpu = 0, activeContracts = 0, totalRevenue = 0,
        topPlans = [], topSellers: rawTopSellers = [],
        plansByMonth = {}, sellersByMonth = {},
        timeline = [], avgChurn = 0
    } = biStats || {};

    const [planPeriod, setPlanPeriod]     = useState(null); // null = todo o tempo
    const [sellerPeriod, setSellerPeriod] = useState(null);

    // ---------- Filtragem de rankings por período ----------
    // Para "todo o tempo" usa os pre-agregados do backend.
    // Para períodos finitos, re-agrega a partir de plansByMonth / sellersByMonth.

    const currentMonthKey = useMemo(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }, []);

    // Retorna os últimos N meses completos (excluindo o mês corrente parcial)
    const getRecentMonths = (n) => {
        const allMonths = Array.from(
            new Set([...Object.keys(plansByMonth), ...Object.keys(sellersByMonth)])
        ).sort().filter(m => m !== currentMonthKey);
        return n === null ? allMonths : allMonths.slice(-n);
    };

    const filteredTopPlans = useMemo(() => {
        if (planPeriod === null) return topPlans || [];
        const months = getRecentMonths(planPeriod);
        const counts = {};
        months.forEach(m => {
            Object.entries(plansByMonth[m] || {}).forEach(([name, count]) => {
                counts[name] = (counts[name] || 0) + count;
            });
        });
        return Object.entries(counts)
            .map(([name, value]) => ({ name: cleanPlanName(name), fullName: name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    }, [planPeriod, plansByMonth, topPlans, currentMonthKey]);

    const filteredTopSellers = useMemo(() => {
        const base = rawTopSellers || [];
        if (sellerPeriod === null) {
            return base.filter(s => !isDefaultSeller(s.fullName)).slice(0, 10);
        }
        const months = getRecentMonths(sellerPeriod);
        const counts = {};
        const fullNames = {};
        months.forEach(m => {
            Object.entries(sellersByMonth[m] || {}).forEach(([name, data]) => {
                counts[name] = (counts[name] || 0) + data.count;
                fullNames[name] = data.fullName;
            });
        });
        return Object.entries(counts)
            .map(([name, value]) => ({ name, fullName: fullNames[name] || name, value }))
            .filter(s => !isDefaultSeller(s.fullName))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    }, [sellerPeriod, sellersByMonth, rawTopSellers, currentMonthKey]);

    // ---------- Evolução de Ativações: média acumulada mês a mês ----------
    const timelineWithAvg = useMemo(() => {
        let runningSum = 0;
        return (timeline || []).map((entry, i) => {
            runningSum += entry.vendas || 0;
            return {
                ...entry,
                mediaAcumulada: Math.round((runningSum / (i + 1)) * 10) / 10,
            };
        });
    }, [timeline]);

    const avgActivacoes = timelineWithAvg.length > 0
        ? Math.round(timelineWithAvg[timelineWithAvg.length - 1].mediaAcumulada)
        : 0;

    // ---------- Previsão de crescimento (5 anos / semestral) ----------
    const now = new Date();
    const monthlyChurnRate = (avgChurn || 0) / 100;
    const annualInflation = 0.0586;
    const semiAnnualInflation = Math.pow(1 + annualInflation, 0.5) - 1;
    const avgActivations = timeline?.length > 0
        ? timeline.reduce((acc, curr) => acc + (curr.vendas || 0), 0) / timeline.length
        : 0;

    const forecastData = [];
    let projectedBase = activeContracts || 0;
    let accumulatedInflationFactor = 1;

    for (let i = 1; i <= 10; i++) {
        const forecastDate = new Date(now.getFullYear(), now.getMonth() + i * 6, 1);
        const label = `${String(forecastDate.getMonth() + 1).padStart(2, '0')}/${forecastDate.getFullYear()}`;
        const semesterActivations = Math.round(avgActivations * 6);
        const semesterCancellations = Math.round(projectedBase * monthlyChurnRate * 6);
        const netGrowth = semesterActivations - semesterCancellations;
        projectedBase = Math.max(0, projectedBase + netGrowth);
        const nominalRevenue = projectedBase * (arpu || 0);
        accumulatedInflationFactor *= (1 + semiAnnualInflation);
        const realRevenue = nominalRevenue / accumulatedInflationFactor;
        forecastData.push({
            period: label,
            base: Math.round(projectedBase),
            receitaNominal: Math.round(nominalRevenue),
            receitaReal: Math.round(realRevenue),
            cancelamentos: semesterCancellations
        });
    }

    // ---------- Tooltips ----------
    const tooltipStyle = {
        backgroundColor: isDark ? '#1e293b' : '#fff',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        color: isDark ? '#fff' : '#000'
    };

    const CustomTooltipForecast = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div className={`p-3 border rounded shadow-md ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold mb-2">Período: {label}</p>
                {payload.map((entry, i) => (
                    <p key={i} style={{ color: entry.color }}>
                        {entry.name}: {entry.name.includes('Receita') ? formatCurrency(entry.value) : entry.value.toLocaleString('pt-BR')}
                    </p>
                ))}
            </div>
        );
    };

    const CustomTooltipPlans = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const data = payload[0].payload;
        return (
            <div className={`p-2 border rounded shadow-md ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold">{data.fullName}</p>
                <p>Vendas: {data.value}</p>
            </div>
        );
    };

    const CustomTooltipSellers = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const data = payload[0].payload;
        return (
            <div className={`p-2 border rounded shadow-md ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold">{data.fullName}</p>
                <p>Vendas: {data.value}</p>
            </div>
        );
    };

    const CustomTooltipActivations = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div className={`p-2 border rounded shadow-md ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold mb-1">{label}</p>
                {payload.map((entry, i) => (
                    <p key={i} style={{ color: entry.color }}>{entry.name}: {entry.value}</p>
                ))}
            </div>
        );
    };

    const truncateString = (str, num) => str?.length > num ? str.slice(0, num) + '...' : str;

    if (!biStats) return <div className="p-4 text-center">Nenhum dado comercial disponível.</div>;

    return (
        <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                    icon={<Users className="h-6 w-6 text-blue-600 dark:text-blue-400"/>}
                    title="Contratos Ativos"
                    value={activeContracts?.toLocaleString('pt-BR')}
                    description="Base atual de clientes"
                    colorClass="bg-blue-100 dark:bg-blue-900/50"
                />
                <StatCard
                    icon={<DollarSign className="h-6 w-6 text-green-600 dark:text-green-400"/>}
                    title="Ticket Médio (ARPU)"
                    value={formatCurrency(arpu)}
                    description="Faturamento médio por cliente"
                    colorClass="bg-green-100 dark:bg-green-900/50"
                />
                <StatCard
                    icon={<TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400"/>}
                    title="Receita Recorrente"
                    value={formatCurrency(totalRevenue)}
                    description="Receita total estimada (Mensal)"
                    colorClass="bg-purple-100 dark:bg-purple-900/50"
                />
            </div>

            {/* Rankings com filtros de período */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Planos Mais Vendidos */}
                <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Planos Mais Vendidos (Top 10)</h3>
                        <PeriodFilterDropdown value={planPeriod} onChange={setPlanPeriod} isDark={isDark} />
                    </div>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={filteredTopPlans} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
                                <XAxis type="number" tick={{ fill: tickColor }} />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    width={150}
                                    tickFormatter={(v) => truncateString(v, 20)}
                                    tick={{ fill: tickColor, fontSize: 12 }}
                                />
                                <Tooltip cursor={{ fill: 'rgba(100,116,139,0.1)' }} content={<CustomTooltipPlans />} />
                                <Bar dataKey="value" name="Vendas" radius={[0, 4, 4, 0]}>
                                    {filteredTopPlans.map((_, index) => (
                                        <Cell key={`cell-plan-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Ranking de Vendedores */}
                <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Ranking de Vendedores (Top 10)</h3>
                        <PeriodFilterDropdown value={sellerPeriod} onChange={setSellerPeriod} isDark={isDark} />
                    </div>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={filteredTopSellers} layout="vertical" margin={{ top: 5, right: 40, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
                                <XAxis type="number" tick={{ fill: tickColor }} />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    width={130}
                                    tick={{ fill: tickColor, fontSize: 12 }}
                                />
                                <Tooltip cursor={{ fill: 'rgba(100,116,139,0.1)' }} content={<CustomTooltipSellers />} />
                                <Bar dataKey="value" name="Vendas" radius={[0, 4, 4, 0]}>
                                    {filteredTopSellers.map((_, index) => (
                                        <Cell key={`cell-seller-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Evolução de Ativações com linha de média */}
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Evolução de Ativações</h3>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isDark ? 'bg-slate-700 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                        Média do período: {avgActivacoes.toLocaleString('pt-BR')} ativações/mês
                    </span>
                </div>
                <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={timelineWithAvg} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis dataKey="month" tick={{ fill: tickColor }} />
                            <YAxis tick={{ fill: tickColor }} />
                            <Tooltip contentStyle={tooltipStyle} content={<CustomTooltipActivations />} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="vendas"
                                name="Novas Ativações"
                                stroke="#3b82f6"
                                strokeWidth={3}
                                activeDot={{ r: 8 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="mediaAcumulada"
                                name="Média Acumulada"
                                stroke="#f59e0b"
                                strokeWidth={2}
                                strokeDasharray="6 3"
                                dot={false}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Previsão de Crescimento (5 Anos) */}
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-bold mb-4 text-slate-800 dark:text-slate-200">Previsão de Crescimento (5 Anos)</h3>
                <div className="h-96 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={forecastData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis dataKey="period" tick={{ fill: tickColor }} interval={1} angle={-30} textAnchor="end" height={50} />
                            <YAxis yAxisId="left" tick={{ fill: tickColor }} />
                            <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} tick={{ fill: tickColor }} />
                            <Tooltip content={<CustomTooltipForecast />} />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="base" name="Base de Usuários Projetada" stroke="#8b5cf6" strokeWidth={3} activeDot={{ r: 8 }} />
                            <Line yAxisId="left" type="monotone" dataKey="cancelamentos" name="Cancelamentos Projetados" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" />
                            <Line yAxisId="right" type="monotone" dataKey="receitaNominal" name="Receita Nominal (Crescimento Base)" stroke="#3b82f6" strokeWidth={2} strokeDasharray="3 3" />
                            <Line yAxisId="right" type="monotone" dataKey="receitaReal" name="Receita Real (Ajustada IPCA)" stroke="#10b981" strokeWidth={3} activeDot={{ r: 8 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
